import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Table, Button, Modal, Form, Alert, Badge, Row, Col } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import PageHeader from '../components/PageHeader.jsx';
import { fmtMoney, fmtDate } from '../lib/format.js';

const INTERVAL_LABELS = {
  day: 'Tag', week: 'Woche', month: 'Monat', quarter: 'Quartal', year: 'Jahr'
};

const EMPTY_DRAFT = {
  customer_id: '',
  name: '',
  interval_unit: 'month',
  interval_count: 1,
  vat_rate: 19,
  auto_issue: false,
  auto_send_email: false,
  starts_at: new Date().toISOString().slice(0, 10),
  next_invoice_at: new Date().toISOString().slice(0, 10),
  ends_at: '',
  note: '',
  items: [{ description: '', quantity: 1, unit: '', unit_price: '' }]
};

export default function SubscriptionsPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  const canWrite = auth.can('subscriptions', 'create');

  const subs = useQuery({
    queryKey: ['subscriptions'],
    queryFn: () => api.get('/subscriptions').then(r => r.data.items)
  });
  const customers = useQuery({
    queryKey: ['customers-min'],
    queryFn: () => api.get('/customers', { params: { limit: 1000 } }).then(r => r.data.items)
  });

  const [showModal, setShowModal] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [error, setError] = useState(null);

  const open = () => { setDraft(EMPTY_DRAFT); setError(null); setShowModal(true); };
  const close = () => setShowModal(false);

  const create = useMutation({
    mutationFn: () => {
      const body = {
        customer_id: draft.customer_id,
        name: draft.name,
        interval_unit: draft.interval_unit,
        interval_count: parseInt(draft.interval_count, 10),
        vat_rate: parseFloat(draft.vat_rate),
        auto_issue: draft.auto_issue,
        auto_send_email: draft.auto_send_email,
        starts_at: draft.starts_at,
        next_invoice_at: draft.next_invoice_at,
        ends_at: draft.ends_at || null,
        note: draft.note || null,
        items: draft.items
          .filter(it => it.description && it.unit_price)
          .map(it => ({
            description: it.description,
            quantity: parseFloat(it.quantity),
            unit: it.unit || null,
            unit_price_cents: Math.round(parseFloat(it.unit_price) * 100)
          }))
      };
      return api.post('/subscriptions', body).then(r => r.data);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['subscriptions'] }); close(); },
    onError: (e) => setError(e.response?.data?.message || JSON.stringify(e.response?.data?.details || e.response?.data))
  });

  const pause = useMutation({
    mutationFn: ({ id, paused }) => api.post(`/subscriptions/${id}/${paused ? 'resume' : 'pause'}`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscriptions'] })
  });
  const cancel = useMutation({
    mutationFn: (id) => api.post(`/subscriptions/${id}/cancel`).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscriptions'] })
  });
  const del = useMutation({
    mutationFn: (id) => api.delete(`/subscriptions/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscriptions'] }),
    onError: (e) => alert(e.response?.data?.message || e.message)
  });
  const runNow = useMutation({
    mutationFn: () => api.post('/subscriptions/run-due').then(r => r.data),
    onSuccess: (r) => {
      const created = (r.items || []).filter(x => x.ok).length;
      alert(`${created} Rechnung(en) aus fälligen Abos erzeugt.`);
      qc.invalidateQueries({ queryKey: ['subscriptions'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
    }
  });

  const customerById = Object.fromEntries((customers.data || []).map(c => [c.id, c]));

  const setItem = (i, key, val) => {
    setDraft(d => {
      const items = [...d.items];
      items[i] = { ...items[i], [key]: val };
      return { ...d, items };
    });
  };
  const addItem = () => setDraft(d => ({ ...d, items: [...d.items, { description: '', quantity: 1, unit: '', unit_price: '' }] }));
  const removeItem = (i) => setDraft(d => ({ ...d, items: d.items.filter((_, idx) => idx !== i) }));

  return (
    <>
      <PageHeader title="Abos / Wiederkehrende Rechnungen"
        subtitle="Erzeugt automatisch Rechnungen nach Intervall"
        actions={
          <>
            <Button variant="outline-secondary" onClick={() => runNow.mutate()} disabled={runNow.isLoading}
              title="Scheduler jetzt manuell auslösen — erzeugt Rechnungen für alle fälligen Abos">
              <Icon icon="tabler:player-play" className="me-1" />Jetzt prüfen
            </Button>
            {canWrite && <Button onClick={open}><Icon icon="tabler:plus" className="me-1" />Neues Abo</Button>}
          </>
        } />

      <Card className="card-flat">
        <Card.Body className="p-0">
          {subs.isLoading && <div className="p-4 text-muted">Lade…</div>}
          {subs.data && subs.data.length === 0 && (
            <div className="empty-state">
              <Icon icon="tabler:repeat-off" className="empty-icon" />
              <div>Noch keine Abos angelegt.</div>
            </div>
          )}
          {subs.data && subs.data.length > 0 && (
            <Table hover className="mb-0 align-middle">
              <thead><tr>
                <th>Name</th><th>Kunde</th><th>Intervall</th>
                <th>Nächste Rechnung</th><th>Auto</th><th>Status</th><th></th>
              </tr></thead>
              <tbody>
                {subs.data.map(s => {
                  const c = customerById[s.customer_id];
                  const status = s.cancelled_at ? 'Gekündigt' : s.paused_at ? 'Pausiert' : 'Aktiv';
                  const variant = s.cancelled_at ? 'danger' : s.paused_at ? 'warning' : 'success';
                  return (
                    <tr key={s.id}>
                      <td><span className="fw-semibold">{s.name}</span></td>
                      <td>{c?.name || <span className="text-muted">—</span>}</td>
                      <td className="small">alle {s.interval_count} {INTERVAL_LABELS[s.interval_unit]}</td>
                      <td className="small">{fmtDate(s.next_invoice_at)}</td>
                      <td>
                        {s.auto_issue && <Badge bg="" className="badge-soft-primary me-1">Ausstellen</Badge>}
                        {s.auto_send_email && <Badge bg="" className="badge-soft-primary">Mail</Badge>}
                      </td>
                      <td><Badge bg="" className={`badge-soft-${variant}`}>{status}</Badge></td>
                      <td className="text-end pe-3">
                        {!s.cancelled_at && (
                          <>
                            <Button size="sm" variant="outline-secondary" className="me-1"
                              title={s.paused_at ? 'Fortsetzen' : 'Pausieren'}
                              onClick={() => pause.mutate({ id: s.id, paused: !!s.paused_at })}>
                              <Icon icon={s.paused_at ? 'tabler:player-play' : 'tabler:player-pause'} />
                            </Button>
                            <Button size="sm" variant="outline-danger" title="Kündigen"
                              onClick={() => confirm('Abo kündigen? Es werden keine weiteren Rechnungen erzeugt.') && cancel.mutate(s.id)}>
                              <Icon icon="tabler:circle-x" />
                            </Button>
                          </>
                        )}
                        {s.cancelled_at && (
                          <Button size="sm" variant="outline-danger" title="Endgültig löschen"
                            onClick={() => confirm('Abo unwiderruflich löschen?') && del.mutate(s.id)}>
                            <Icon icon="tabler:trash" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={close} size="lg" centered>
        <Modal.Header closeButton><Modal.Title>Neues Abo</Modal.Title></Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
          <Row className="g-3">
            <Col md={7}><Form.Group>
              <Form.Label>Kunde *</Form.Label>
              <Form.Select required value={draft.customer_id}
                onChange={(e) => setDraft({ ...draft, customer_id: e.target.value })}>
                <option value="">— wählen —</option>
                {(customers.data || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Form.Select>
            </Form.Group></Col>
            <Col md={5}><Form.Group>
              <Form.Label>Bezeichnung *</Form.Label>
              <Form.Control required value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="z.B. Wartung Pro" />
            </Form.Group></Col>

            <Col md={4}><Form.Group>
              <Form.Label>Intervall</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control type="number" min="1" style={{ width: 70 }} value={draft.interval_count}
                  onChange={(e) => setDraft({ ...draft, interval_count: e.target.value })} />
                <Form.Select value={draft.interval_unit}
                  onChange={(e) => setDraft({ ...draft, interval_unit: e.target.value })}>
                  {Object.entries(INTERVAL_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </Form.Select>
              </div>
            </Form.Group></Col>
            <Col md={4}><Form.Group>
              <Form.Label>Nächste Rechnung</Form.Label>
              <Form.Control type="date" value={draft.next_invoice_at}
                onChange={(e) => setDraft({ ...draft, next_invoice_at: e.target.value })} />
            </Form.Group></Col>
            <Col md={4}><Form.Group>
              <Form.Label>Endet am (optional)</Form.Label>
              <Form.Control type="date" value={draft.ends_at}
                onChange={(e) => setDraft({ ...draft, ends_at: e.target.value })} />
            </Form.Group></Col>

            <Col md={6}><Form.Check type="switch" id="auto-issue"
              label="Automatisch ausstellen (kein Entwurf)"
              checked={draft.auto_issue}
              onChange={(e) => setDraft({ ...draft, auto_issue: e.target.checked })} /></Col>
            <Col md={6}><Form.Check type="switch" id="auto-email"
              label="Automatisch per E-Mail an Kunden"
              checked={draft.auto_send_email}
              disabled={!draft.auto_issue}
              onChange={(e) => setDraft({ ...draft, auto_send_email: e.target.checked })} /></Col>

            <Col md={12}>
              <h6 className="text-uppercase text-muted small mb-2 mt-2" style={{ letterSpacing: '0.05em' }}>
                Positionen
              </h6>
              {draft.items.map((it, i) => (
                <Row className="g-2 mb-2 align-items-center" key={i}>
                  <Col md={5}><Form.Control placeholder="Beschreibung *" value={it.description}
                    onChange={(e) => setItem(i, 'description', e.target.value)} /></Col>
                  <Col md={2}><Form.Control type="number" step="0.01" min="0" placeholder="Menge"
                    value={it.quantity} onChange={(e) => setItem(i, 'quantity', e.target.value)} /></Col>
                  <Col md={2}><Form.Control placeholder="Einheit" value={it.unit}
                    onChange={(e) => setItem(i, 'unit', e.target.value)} /></Col>
                  <Col md={2}><Form.Control type="number" step="0.01" placeholder="Netto €" value={it.unit_price}
                    onChange={(e) => setItem(i, 'unit_price', e.target.value)} /></Col>
                  <Col md={1}><Button size="sm" variant="outline-danger" onClick={() => removeItem(i)} disabled={draft.items.length === 1}>
                    <Icon icon="tabler:trash" />
                  </Button></Col>
                </Row>
              ))}
              <Button size="sm" variant="outline-secondary" onClick={addItem}>
                <Icon icon="tabler:plus" className="me-1" />Position
              </Button>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={close}>Abbrechen</Button>
          <Button onClick={() => create.mutate()} disabled={!draft.customer_id || !draft.name || create.isLoading}>
            Anlegen
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
